import { PageLayout } from '../../layout/PageLayout'
import Column from '@app/components/layout/Column'
import Row from '@app/components/layout/Row'
import { useTranslations } from '@i18n/useLanguage'
import { useState, useMemo, useEffect } from 'react'
import { SocialShareModal } from '@app/components/blocks/modal/SocialShareModal'
import { MapPageHeader } from '@app/components/blocks/pageHeader/MapPageHeader'
import {
  faPlus,
  faCheck,
  faArrowLeft,
} from '@fortawesome/pro-regular-svg-icons'
import { MunicipalityTabContent } from './regionTabs/MunicipalityTabContent'
import { ItemListCard } from '@app/components/blocks/card/ItemListCard'
import { navigate } from 'wouter/use-browser-location'
import { pages } from '@app/util/links'
import { MunicipalityViewModel } from 'functions/app/geography/view/municipality'
import { Head, useAuthState, useClient, useQuery } from '@based/sdk/react'
import defaultHousingFormPicture from '@app/assets/defaultHousingFormPicture.png'
import { enrich } from '@i18n/localizeHelper'
import { useAuthModal } from '@app/util/hooks/useAuthModal'
import { LockedPageCover } from '@app/components/blocks/lockedPageCover/LockedPageCover'
import useMediaQuery from '@app/util/useMediaQuery'
import { HousingFormListRegionalModel } from 'functions/app/housing-form/list-regional'
import { ServiceProviderListRegionalModel } from 'functions/app/service-provider/list-regional'
import { SERVICE_PROVIDER_TYPES } from '@schema/serviceProvider'
import pentagon from '@app/assets/images/pentagon.svg'
import leavesStacked from '@app/assets/images/leaves_stacked.svg'
import nutScrew from '@app/assets/images/nut_screw.svg'
import { CtaCard } from '@app/components/blocks/card/CtaCard'
import type { RawPlotRegistryItem } from 'functions/app/plot-registry/list-active'
import { TopicCommunityListRegionalModel } from 'functions/app/topic-community/list-regional'

export type MunicipalityPageProps = {
  slug: string
}

export const MunicipalityPage = ({ slug }: MunicipalityPageProps) => {
  const isMobile = useMediaQuery()
  const [isShareModalOpen, setIsShareModalOpen] = useState(false)
  const [mounted, setMounted] = useState(false)
  const translations = useTranslations().app
  const municipalityQuery = useQuery('view-municipality', { slug })
  const { withAuthCheck } = useAuthModal()
  const client = useClient()
  const authState = useAuthState()

  useEffect(() => {
    setMounted(true)
  }, [])

  const municipality: MunicipalityViewModel = municipalityQuery.data
  const pageManager = municipality?.pageManager
  const followerCountQuery = useQuery('count-followers', {
    entityType: 'municipality',
    entityIds: municipality?.id ? [municipality.id] : [],
  })

  const housingFormsQuery = useQuery('list-regional-housing-forms', {
    municipalityId: municipality?.id,
    top: 5,
  })
  const housingForms = useMemo(
    () => (housingFormsQuery.data ?? []) as HousingFormListRegionalModel[],
    [housingFormsQuery.data],
  )
  const housingFormItems = useMemo(
    () =>
      housingForms
        .slice(0, 5)
        .map((hf) => ({
          image: hf.images?.[0]?.src ?? defaultHousingFormPicture,
          title: hf.title,
          subtitle: enrich(
            translations.common.followersAmount,
            hf.followers.count,
          ),
          followerCount: hf.followers.count,
          onClick: () => navigate(pages.housingForm(hf.slug)),
        }))
        .sort((a, b) => b.followerCount - a.followerCount),
    [housingForms, translations.common.followersAmount],
  )

  const { data: activePlotRegistries } = useQuery(
    'list-active-plot-registries',
    {
      municipalitySlug: slug,
    },
  )

  const activePlotRegistry = useMemo(() => {
    if (!activePlotRegistries) {
      return null
    }
    const items = (activePlotRegistries as RawPlotRegistryItem[]).filter(
      (k) => k.applicationWindows?.length > 0,
    )
    if (items.length === 0) {
      return null
    }
    return { ...items[0], applicationWindow: items[0].applicationWindows[0] }
  }, [activePlotRegistries])

  const serviceProvidersQuery = useQuery('list-regional-service-providers', {
    municipalityId: municipality?.id,
    top: 5,
    types: [
      SERVICE_PROVIDER_TYPES.network,
      SERVICE_PROVIDER_TYPES.supportGroup,
    ],
  })
  const serviceProviders = useMemo(
    () =>
      (serviceProvidersQuery.data ?? []) as ServiceProviderListRegionalModel[],
    [serviceProvidersQuery.data],
  )
  const serviceProviderItems = useMemo(
    () =>
      serviceProviders
        .slice(0, 5)
        .map((sp) => ({
          image: sp.avatar?.src ?? '',
          title: sp.name,
          subtitle: enrich(
            translations.common.followersAmount,
            sp.followers.count,
          ),
          followerCount: sp.followers.count,
          onClick: () => navigate(pages.serviceProvider(sp.slug)),
        }))
        .sort((a, b) => b.followerCount - a.followerCount),
    [serviceProviders, translations.common.followersAmount],
  )

  const topicCommunitiesQuery = useQuery('list-regional-topic-communities', {
    municipalityId: municipality?.id,
    top: 5,
  })
  const topicCommunities = useMemo(
    () =>
      (topicCommunitiesQuery.data ?? []) as TopicCommunityListRegionalModel[],
    [topicCommunitiesQuery.data],
  )
  const topicCommunityItems = useMemo(
    () =>
      topicCommunities.map((tc) => ({
        image: tc.banner?.src ?? '',
        title: tc.name,
        onClick: () => navigate(pages.topicCommunity(tc.slug)),
      })),
    [topicCommunities],
  )

  const followMunicipality = () => {
    void withAuthCheck(async () => {
      await client.call('follow-municipality', {
        municipalityId: municipality.id,
      })
    })
  }

  const isFollowingQuery = useQuery(authState?.userId ? 'is-following' : null, {
    entityType: 'municipality',
    entityId: municipality?.id,
    userId: Number(authState?.userId),
  })
  const isFollowing = mounted && ((isFollowingQuery.data as boolean) ?? false)
  // Use stable icon and label until after mount to avoid hydration mismatch (auth state differs server vs client)
  const followButtonIcon = mounted ? (isFollowing ? faCheck : faPlus) : faPlus
  const followButtonLabel = mounted
    ? isFollowing
      ? translations.common.following
      : translations.common.follow
    : translations.common.follow

  const isMunicipalityLocked =
    !municipality?.crowdbuildingPartner &&
    !municipality?.region?.crowdbuildingPartner

  if (!municipality) {
    return (
      <>
        <PageLayout gap="2rem">
          <Row style={{ alignItems: 'flex-start' }} gap="3rem">
            <Column
              gap="1.5rem"
              alignItems="stretch"
              style={{ flex: '2 1 auto' }}
            >
              <MapPageHeader title={translations.common.loading} />
            </Column>
          </Row>
        </PageLayout>
      </>
    )
  }

  const mapHeader = (
    <MapPageHeader
      title={municipality.name}
      subtitle={pageManager ? translations.regionPage.managedBy : null}
      profile={pageManager}
      hasBadge={municipality.crowdbuildingPartner}
      buttons={[
        {
          label: translations.common.share,
          onClick: () => setIsShareModalOpen(true),
          variant: 'secondary',
          size: isMobile ? 'small' : 'medium',
        },
        {
          label: followButtonLabel,
          onClick: followMunicipality,
          variant: 'primary',
          size: isMobile ? 'small' : 'medium',
          icon: followButtonIcon,
          iconPosition: 'left',
        },
      ]}
      onClick={
        pageManager
          ? () => navigate(pages.regionalBusinessProfile(pageManager.slug))
          : null
      }
      followers={municipality.followers}
      totalFollowers={followerCountQuery.data?.[0] ?? 0}
      coordinates={
        municipality.mapCoordinates?.lat && municipality.mapCoordinates?.long
          ? {
              lat: municipality.mapCoordinates.lat,
              long: municipality.mapCoordinates.long,
            }
          : null
      }
      zoom={9}
    />
  )

  if (isMobile) {
    return (
      <>
        <PageLayout gap="0rem" standardPageTitle={false}>
          <Head>
            <title>{`${municipality.name} | CrowdBuilding`}</title>
          </Head>
          <Column alignItems="stretch">
            {mapHeader}
            <ItemListCard
              title={`${translations.regionPage.popularHousingFormsIn} ${municipality.name}`}
              items={housingFormItems}
            />
            {serviceProviderItems.length > 0 && (
              <ItemListCard
                title={`${translations.regionPage.networksAndSupportGroupsIn} ${municipality.name}`}
                items={serviceProviderItems}
              />
            )}
            {activePlotRegistry && (
              <CtaCard
                title="Schrijf je in voor de Kaartenbak"
                subtitle={`Om kans te maken op (toekomstige) kavels in de gemeente ${municipality.name} moeten initiatieven zich inschrijven voor de CrowdBuilding Kaartenbak.`}
                illustration={[pentagon, leavesStacked, nutScrew]}
                illustrationColors={[
                  'var(--fine-pink)',
                  'var(--fine-pink)',
                  'var(--fine-pink)',
                ]}
                linkText="Schrijf je initiatief in"
                linkHref={`${pages.plotRegistry}?plotRegistryId=${activePlotRegistry.id}`}
                onClick={() =>
                  navigate(
                    `${pages.plotRegistry}?plotRegistryId=${activePlotRegistry.id}`,
                  )
                }
                border={false}
                style={{
                  backgroundColor: 'var(--shades-pink-100)',
                  borderRadius: '1.875rem',
                }}
                linkAsButton={true}
                buttonVariant="primary"
              />
            )}
          </Column>
          <MunicipalityTabContent municipality={municipality} />
        </PageLayout>

        <SocialShareModal
          isOpen={isShareModalOpen}
          onClose={() => setIsShareModalOpen(false)}
          title={municipality.name}
        />

        {isMunicipalityLocked &&
          (isFollowing ? (
            <LockedPageCover
              title={translations.regionPage.lockedMunicipalityFollowed}
              body={translations.regionPage.lockedMunicipalityBodyFollowed}
              buttonProps={[
                {
                  variant: 'secondary',
                  size: 'medium',
                  label: translations.common.back,
                  onClick: () => navigate(pages.regions),
                  icon: faArrowLeft,
                  iconPosition: 'left',
                },
                {
                  variant: 'primary',
                  size: 'medium',
                  label: translations.regionPage.pageFollowed,
                  onClick: followMunicipality,
                  icon: faCheck,
                  iconPosition: 'left',
                },
              ]}
              style={{
                inset: '3rem 0 0 0',
              }}
            />
          ) : (
            <LockedPageCover
              title={enrich(
                translations.regionPage.lockedMunicipalityHeader,
                municipality.name,
              )}
              body={enrich(
                translations.regionPage.lockedMunicipalityBody,
                municipality.name,
              )}
              buttonProps={[
                {
                  variant: 'primary',
                  size: 'medium',
                  label: translations.regionPage.followThePage,
                  onClick: followMunicipality,
                },
              ]}
              style={{
                inset: '3rem 0 0 0',
              }}
            />
          ))}
      </>
    )
  }

  return (
    <>
      <PageLayout gap="2rem" standardPageTitle={false}>
        <Head>
          <title>{`${municipality.name} | CrowdBuilding`}</title>
        </Head>
        <Row style={{ alignItems: 'flex-start' }} gap="3rem">
          <Column
            gap="1.5rem"
            alignItems="stretch"
            style={{ flex: '2 1 auto' }}
          >
            {mapHeader}
            <Row gap="1.5rem" style={{ alignItems: 'flex-start' }}>
              <Column
                alignItems="stretch"
                gap="3.5rem"
                style={{ flex: '2 1 auto' }}
              >
                <MunicipalityTabContent municipality={municipality} />
              </Column>
            </Row>
          </Column>
          <Column
            alignItems="stretch"
            style={{ flex: '1 1 23rem', minWidth: '23rem' }}
          >
            {housingFormItems.length > 0 && (
              <ItemListCard
                title={`${translations.regionPage.popularHousingFormsIn} ${municipality.name}`}
                items={housingFormItems}
              />
            )}
            {serviceProviderItems.length > 0 && (
              <ItemListCard
                title={`${translations.regionPage.networksAndSupportGroupsIn} ${municipality.name}`}
                items={serviceProviderItems}
              />
            )}
            {topicCommunityItems.length > 0 && (
              <ItemListCard
                title={`${translations.regionPage.topicCommunitiesIn} ${municipality.name}`}
                items={topicCommunityItems}
              />
            )}

            {activePlotRegistry && (
              <CtaCard
                title="Schrijf je in voor de Kaartenbak"
                subtitle={`Om kans te maken op (toekomstige) kavels in de gemeente ${municipality.name} moeten initiatieven zich inschrijven voor de CrowdBuilding Kaartenbak.`}
                illustration={[pentagon, leavesStacked, nutScrew]}
                illustrationColors={[
                  'var(--fine-pink)',
                  'var(--fine-pink)',
                  'var(--fine-pink)',
                ]}
                linkText="Schrijf je initiatief in"
                linkHref={`${pages.plotRegistry}?plotRegistryId=${activePlotRegistry.id}`}
                onClick={() =>
                  navigate(
                    `${pages.plotRegistry}?plotRegistryId=${activePlotRegistry.id}`,
                  )
                }
                border={false}
                style={{
                  backgroundColor: 'var(--shades-pink-100)',
                  borderRadius: '1.875rem',
                }}
                linkAsButton={true}
                buttonVariant="primary"
              />
            )}
          </Column>
        </Row>
      </PageLayout>

      <SocialShareModal
        isOpen={isShareModalOpen}
        onClose={() => setIsShareModalOpen(false)}
        title={municipality.name}
      />

      {isMunicipalityLocked &&
        (isFollowing ? (
          <LockedPageCover
            title={translations.regionPage.lockedMunicipalityFollowed}
            body={translations.regionPage.lockedMunicipalityBodyFollowed}
            buttonProps={[
              {
                variant: 'secondary',
                size: 'medium',
                label: translations.common.back,
                onClick: () => navigate(pages.regions),
                icon: faArrowLeft,
                iconPosition: 'left',
              },
              {
                variant: 'primary',
                size: 'medium',
                label: translations.regionPage.pageFollowed,
                onClick: followMunicipality,
                icon: faCheck,
                iconPosition: 'left',
              },
            ]}
            style={{
              inset: 'var(--header-height) 0 0 0',
            }}
          />
        ) : (
          <LockedPageCover
            title={enrich(
              translations.regionPage.lockedMunicipalityHeader,
              municipality.name,
            )}
            body={enrich(
              translations.regionPage.lockedMunicipalityBody,
              municipality.name,
            )}
            buttonProps={[
              {
                variant: 'primary',
                size: 'medium',
                label: translations.regionPage.followThePage,
                onClick: followMunicipality,
              },
            ]}
            style={{
              inset: 'var(--header-height) 0 0 0',
            }}
          />
        ))}
    </>
  )
}
